import { boundsOf, hitTestRectangle } from './snap.js';

/** 可被组合工具框选并编组的类型（不含画框本身） */
export const GROUPABLE_TYPES = new Set([
  'card',
  'rectangle',
  'ellipse',
  'text',
  'freedraw',
  'arrow',
]);

export function elementCenter(el) {
  const b = boundsOf(el);
  return { x: b.cx, y: b.cy };
}

export function isCenterInsideRect(el, rx, ry, rw, rh) {
  const { x, y } = elementCenter(el);
  return x >= rx && x <= rx + rw && y >= ry && y <= ry + rh;
}

export function elementsInRect(elements, rx, ry, rw, rh, options = {}) {
  const types = options.types || GROUPABLE_TYPES;
  const excludeIds = options.excludeIds || new Set();
  const result = [];
  for (const el of elements) {
    if (!el?.type || excludeIds.has(el.id)) continue;
    if (!types.has(el.type)) continue;
    if (isCenterInsideRect(el, rx, ry, rw, rh)) result.push(el);
  }
  return result;
}

function frameArea(frame) {
  return (frame.width || 0) * (frame.height || 0);
}

/** 将元素中心点归属到最小包含画框（画框可嵌套，子画框 frameId 指向父画框） */
export function assignFrameMembership(el, elements) {
  if (!el) return;
  const center = elementCenter(el);
  const elArea = el.type === 'frame' ? frameArea(el) : null;
  let best = null;
  let bestArea = Infinity;
  for (const frame of elements) {
    if (frame.type !== 'frame' || frame.id === el.id) continue;
    const area = frameArea(frame);
    if (area <= 0) continue;
    // 画框只能归属到严格更大的父画框（面积须大于自身），避免大框被挂到小框下
    if (el.type === 'frame' && area <= elArea) continue;
    if (!hitTestRectangle(frame, center.x, center.y, 0)) continue;
    if (area < bestArea) {
      bestArea = area;
      best = frame;
    }
  }
  if (best) el.frameId = best.id;
  else if (el.frameId) delete el.frameId;
}

/** 收集画框内所有后代（含嵌套画框及其子元素） */
export function collectFrameDescendants(frameId, elements, visited = new Set()) {
  const bundle = [];
  for (const el of elements) {
    if (el.frameId !== frameId) continue;
    if (visited.has(el.id)) continue;
    visited.add(el.id);
    bundle.push(el);
    if (el.type === 'frame') {
      bundle.push(...collectFrameDescendants(el.id, elements, visited));
    }
  }
  return bundle;
}

export function syncFrameChildren(frame, elements) {
  if (!frame || frame.type !== 'frame') return;
  for (const el of elements) {
    if (el.id === frame.id) continue;
    const center = elementCenter(el);
    if (hitTestRectangle(frame, center.x, center.y, 0)) {
      assignFrameMembership(el, elements);
    } else if (el.frameId === frame.id) {
      delete el.frameId;
      if (el.type === 'frame') {
        for (const child of elements) {
          if (child.frameId === el.id) assignFrameMembership(child, elements);
        }
      }
    }
  }
}

/** 根据几何关系重建全部 frameId（加载旧画布时修复嵌套画框） */
export function repairAllFrameMembership(elements) {
  for (const el of elements) {
    assignFrameMembership(el, elements);
  }
}

export function translateElement(el, dx, dy) {
  if (!el || !dx && !dy) return;
  if (el.type === 'freedraw' && el.points?.length) {
    for (const p of el.points) {
      p[0] += dx;
      p[1] += dy;
    }
    return;
  }
  if (el.type === 'arrow' && el.points?.length) {
    for (const p of el.points) {
      p[0] += dx;
      p[1] += dy;
    }
    return;
  }
  if (el.x != null && el.y != null) {
    el.x += dx;
    el.y += dy;
  }
}
